/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { DependencyContainer } from "@adapptio/lib-core";
import { IAppInfo } from "./common/AppInfo";
import { ResourceService } from "./resources/ResourceService";
import { BrowserLogger } from "./common/BrowserLogger";
import { EditorConfigService } from "./config/EditorConfigService";
import { StateService } from "./state/StateService";
import { ViewService } from "./view/ViewService";
import { IEditor, IEditorDependencies } from "./IEditor";
import { CommandsService } from "./commands/CommandsService";
import { MenuService } from "./menu/MenuService";
import { KeyBindingService } from "./keyBinding/KeyBindingService";
import { TelemetryService } from "./telemetry/TelemetryService";
import { RuntimeService, TRuntimeRegisterFunction } from "./runtime/RuntimeService";
import { CoreModule } from "./modules/Core/CoreModule";
import { DocumentationModule } from "./modules/Documentation/DocumentationModule";
import { detectOperatingSystem } from "./common/Env";
import { DialogService } from "./dialogs/DialogService";
import { DeploymentService } from "./deployment/DeploymentService";
import { EnvironmentService } from "./environment/EnvironmentService";
import { ToastService } from "./toasts/ToastService";
import { IntegrationService } from "./integration/IntegrationService";
import { AbilityService } from "./ability/AbilityService";
import { AppConfigService } from "./config/AppConfigService";
import { MemoryStorage } from "./memoryStorage/MemoryStorage";
import { ProductSupportService } from "./productSupport/ProductSupportService";
import { EditorRemoteApiService } from "./api/remote/EditorRemoteApiService";
import { TELEMETRY_EVENTS } from "./telemetry/TelemetryConstants";
import { SessionStorageService } from "./sessionStorage/SessionStorageService";
import { AuthProvidersService } from "./authProviders/AuthProvidersService";
import { IProductSupportOpts } from "./productSupport/IProductSupportOpts";
import { AuthenticateDialog } from "./modules/Core/AuthenticateDialog/AuthenticateDialog";
import { ScmService } from "./scm/ScmService";

// For debugging
const _window = (globalThis.window || {}) as Record<string, unknown> & Window;

/**
 *  Editor Options
 */
export interface IEditorOpts {
	apiUrl: string;
	runtimeRegisterFn: TRuntimeRegisterFunction;
	productSupport?: IProductSupportOpts;
	accessToken?: string;
}

export enum EDITOR_STATE {
	INITIALIZING = "initializing",
	AWAITING_AUTH = "awaiting_auth",
	READY = "ready",
	ERROR = "error",
	INIT_AUTH_FAILED = "auth_failed"
}

export type TEditorModuleRegisterFunction = (dc: IEditor) => void;

/**
 *  Editor Main Class
 */
export class Editor extends DependencyContainer<IEditorDependencies> implements IEditor {
	private initState: EDITOR_STATE;

	/**
	 * On initialized event
	 * WARNING: Is called even when init has failed
	 */
	public onInitStateChange = createEventEmitter<void>();

	/** Event emitted when editor was successfully initialized */
	public onReady = createEventEmitter<void>();

	/** Event emitted when editor is being disposed */
	public onTeardown = createEventEmitter<void>();

	/**
	 * Editor Constructor
	 *
	 * @param opts Options
	 */
	public constructor(private opts: IEditorOpts) {
		super();

		// Assign self to global window for debugging
		_window.__editorInstance = this;

		this.initState = EDITOR_STATE.INITIALIZING;

		this.register("logger", new BrowserLogger(" Editor"));
		this.register(
			"telemetryService",
			new TelemetryService(this, {
				activityInterval: 60000
			})
		);
		this.register("memoryStorage", new MemoryStorage());
		this.register("editorConfigService", new EditorConfigService(this));
		this.register(
			"stateService",
			new StateService(this, {
				storagekey: "adpt-editor"
			})
		);
		this.register("commandsService", new CommandsService(this, {}));
		this.register("dialogService", new DialogService(this, {}));
		this.register("toastService", new ToastService(this, {}));
		this.register("menuService", new MenuService(this, {}));
		this.register("keyBindingService", new KeyBindingService(this, {}));

		this.register(
			"apiService",
			new EditorRemoteApiService(this, {
				apiUrl: opts.apiUrl
			})
		);

		this.register("appConfigService", new AppConfigService(this));
		this.register("resourceService", new ResourceService(this));
		this.register("environmentService", new EnvironmentService(this));
		this.register(
			"runtimeService",
			new RuntimeService(this, {
				registerFn: opts.runtimeRegisterFn
			})
		);
		this.register("integrationService", new IntegrationService(this));
		this.register("sessionStorageService", new SessionStorageService(this));
		this.register("authProvidersService", new AuthProvidersService(this));
		this.register("viewService", new ViewService(this, {}));
		this.register("scmService", new ScmService(this));
		this.register("deploymentService", new DeploymentService(this));
		this.register("abilityService", new AbilityService(this));
		this.register("productSupportService", new ProductSupportService(this, opts.productSupport ?? {}));

		// Enable core module
		this.enable(CoreModule);
		this.enable(DocumentationModule);

		// Assign document body styles
		if (_window?.document?.body) {
			const os = detectOperatingSystem();

			_window.document.body.classList.add("is-adpt-editor");
			_window.document.body.classList.add("adpt-editor-os-" + os);
		}

		this.get("telemetryService").trackEvent(TELEMETRY_EVENTS.lifecycle_editor_loaded);
	}

	/**
	 * Enables editor module
	 *
	 * @param moduleRegisterFunction Module register function
	 */
	public enable(moduleRegisterFunction: TEditorModuleRegisterFunction): void {
		moduleRegisterFunction(this);
	}

	/**
	 * Returns editor init state
	 */
	public getInitState(): EDITOR_STATE {
		return this.initState;
	}

	/**
	 * Returns initial access token provided in options
	 * @return Access token or undefined
	 */
	public getInitialAccessToken(): string | undefined {
		return this.opts.accessToken;
	}

	/**
	 * Loads and sets app info
	 */
	private async loadAppInfo() {
		const apiService = this.get("apiService");
		const appMetaData = await apiService.getAppMeta();

		const appInfo: IAppInfo = {
			name: appMetaData.appName,
		};

		this.register("appInfo", appInfo);

		this.get("logger").debug("App Info:", appInfo);
	}

	/**
	 * Initializes the editor
	 * Must be called after tryRestoreSession or auth
	 */
	public async init(): Promise<void> {
		if (this.initState !== EDITOR_STATE.INITIALIZING) {
			throw new Error("Not awaiting for initialization.");
		}

		// Restore state
		this.get("stateService").restore();

		const apiService = this.get("apiService");

		if (this.opts.accessToken) {
			try {
				await apiService.auth(this.opts.accessToken);
				await this.finishInit();
			} catch (err) {
				this.get("logger").error("Failed to authenticate with provided access token:", err);

				this.initState = EDITOR_STATE.INIT_AUTH_FAILED;
				emitEvent(this.onInitStateChange);
			}
		} else {
			const sessionRestored = await apiService.tryRestoreSession();

			if (sessionRestored) {
				await this.finishInit();
			} else {
				this.initState = EDITOR_STATE.AWAITING_AUTH;
				emitEvent(this.onInitStateChange);

				this.get("dialogService").open(AuthenticateDialog, "authenticate-dialog", {});
			}
		}
	}

	/**
	 * Logs the user in using access token and finishes initialization
	 * 
	 * @param accessToken Access token
	 */
	public async authenticate(accessToken: string): Promise<void> {
		const apiService = this.get("apiService");
		await apiService.auth(accessToken);

		if (this.initState === EDITOR_STATE.AWAITING_AUTH) {
			this.initState = EDITOR_STATE.INITIALIZING;
			emitEvent(this.onInitStateChange);

			await this.finishInit();
		}
	}

	private async finishInit() {
		if (this.initState !== EDITOR_STATE.INITIALIZING) {
			throw new Error("Already initialized or not awaiting for initialization.");
		}

		const logger = this.get("logger");

		logger.info("Initializing...");
		logger.debug("Config:", this.opts);

		try {
			// Check if we have valid session
			const editorAccount = this.get("apiService").getEditorIdentity();

			if (!editorAccount) {
				throw new Error("Cannot initialize editor because there is no valid session.");
			}

			// Load app info
			await this.loadAppInfo();

			// Init services
			await super.init();

			this.initState = EDITOR_STATE.READY;

			emitEvent(this.onReady);
		} catch (err) {
			logger.error("Failed to init:", err);
			console.dir(err);

			this.initState = EDITOR_STATE.ERROR;
		}

		logger.debug("Initialized with state:", this.initState);
		emitEvent(this.onInitStateChange);
	}

	/**
	 * Destroys the editor instance
	 */
	public async dispose(): Promise<void> {
		const logger = this.get("logger");

		logger.debug("Disposing.");

		emitEvent(this.onTeardown);
		removeAllEventListeners(this.onTeardown);
		removeAllEventListeners(this.onInitStateChange);

		await super.dispose();

		// Remove document body styles
		if (_window?.document?.body) {
			const os = detectOperatingSystem();

			_window.document.body.classList.remove("is-adpt-editor");
			_window.document.body.classList.remove("adpt-editor-os-" + os);
		}

		logger.info("Disposed.");
	}
}
